import { Suspense } from 'react'

import { Navigate, Route, Routes } from 'react-router-dom'

import accountTypeRouteWrapper from 'utils/hoc/accountTypeRouteWrapper'
import { featureFlagRouteWrapper } from 'utils/sdks/launchDarklyFeatureFlagRouteWrapper'

import { REDIRECT_AFTER_SWITCH } from 'modals/OrderList/StartOrderModal/RetailerStartOrderModal/RetailerStartOrderModal.component'
import LegacyPage from 'pages/LegacyPage'
import RouterPage from 'pages/RouterPage/RouterPage'
import PrivateRoute from 'routes/PrivateRoute'
import { HOME, NEW_ORDER_REVIEW, SUBMISSIONS_VIEW } from 'routes/paths'
import {
  DEFAULT_ROUTES,
  PRIVATE_ROUTES,
  PUBLIC_ROUTES,
  RouteType,
} from 'routes/routes'

import styles from './PageContent.module.css'
import ExceptionHandler from 'containers/ExceptionHandler'
import FileDownloadHandler from 'containers/FileDownload/FileDownloadHandler.container'
import NewsBanner from 'containers/NewsBanner'
import NotificationHandler from 'containers/NotificationHandler/NotificationHandler.container'
import PageLoader from 'containers/PageLoader/PageLoader.container'
import EarlyAccessWidgetContainer from 'features/EarlyAccessWidget/components/EarlyAccessWidgetContainer/EarlyAccessWidgetContainer'

const PublicPageWrapper = ({ route }: { route: RouteType }) => {
  const {
    element: WrappedComponent,
    path,
    title,
    feature,
    accountTypes,
  } = route

  const base = (
    <RouterPage path={path} title={title}>
      <WrappedComponent />
    </RouterPage>
  )

  const withFeatureFlag = feature
    ? featureFlagRouteWrapper(() => base, { flagKey: feature })
    : base

  const withAccountTypeCheck = accountTypes
    ? accountTypeRouteWrapper(() => withFeatureFlag, { accountTypes })
    : withFeatureFlag

  return withAccountTypeCheck
}

const PrivatePageWrapper = ({ route }: { route: RouteType }) => {
  const {
    element: WrappedComponent,
    path,
    title,
    feature,
    accountTypes,
  } = route

  // TODO:
  // to prevent rerenders of page when only the param has changed,
  // we should memoize this base.
  // sort of like
  // const renderBase = useCallback(
  //   () => (
  //     <RouterPage path={path} title={title}>
  //       <WrappedComponent />
  //     </RouterPage>
  //   ),
  //   [path, title, WrappedComponent],
  // )
  const base = (
    <RouterPage path={path} title={title}>
      <WrappedComponent />
    </RouterPage>
  )

  // and pass renderBase here
  const withFeatureFlag = feature
    ? featureFlagRouteWrapper(() => base, { flagKey: feature })
    : base

  const withAccountTypeCheck = accountTypes
    ? accountTypeRouteWrapper(() => withFeatureFlag, { accountTypes })
    : withFeatureFlag

  return <PrivateRoute>{withAccountTypeCheck}</PrivateRoute>
}

const PageContent = () => {
  const redirectAfterSwitch = window.localStorage.getItem(REDIRECT_AFTER_SWITCH)
  const redirectAfterSwitchInfo = redirectAfterSwitch
    ? JSON.parse(redirectAfterSwitch)
    : null

  return (
    <>
      <NewsBanner newsToDisplay={['upcomingMaintenanceBanner']} />
      <div className={styles.routerOutlet}>
        <EarlyAccessWidgetContainer />
        <Suspense fallback={<></>}>
          <Routes>
            {PRIVATE_ROUTES.map((route) => {
              const shouldRedirect =
                route.path === HOME &&
                (redirectAfterSwitchInfo?.store?.brand?.id ||
                  redirectAfterSwitchInfo?.path?.includes(
                    SUBMISSIONS_VIEW.replace(':submissionId', ''),
                  ) ||
                  redirectAfterSwitchInfo?.path?.includes(
                    NEW_ORDER_REVIEW.replace(':id', ''),
                  ))

              if (shouldRedirect) {
                return (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={
                      <Navigate to={redirectAfterSwitchInfo.path} replace />
                    }
                  />
                )
              }

              return (
                <Route
                  key={route.path}
                  path={route.path}
                  element={<PrivatePageWrapper route={route} />}
                />
              )
            })}

            {PUBLIC_ROUTES.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={<PublicPageWrapper route={route} />}
              />
            ))}

            {DEFAULT_ROUTES.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              />
            ))}

            <Route path="*" element={<LegacyPage />} />
          </Routes>
        </Suspense>
      </div>
      <NotificationHandler />
      <ExceptionHandler />
      <FileDownloadHandler />
      <PageLoader />
    </>
  )
}

export default PageContent
